
import { useState, useEffect } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useCarousel } from "@/hooks/use-carousel";
import { cn } from "@/lib/utils";
import Autoplay from "embla-carousel-autoplay";
import type { AutoplayType } from "embla-carousel-autoplay";

const steps = [
  {
    title: "1. Tanışma ve Analiz",
    content: "İlk görüşmemizde mevcut durumunuzu, hedeflerinizi ve çalışma alışkanlıklarınızı detaylıca analiz ederiz.",
    image: "/kocluk-nedir-materials/analiz.png",
    badge: "Ücretsiz İlk Görüşme"
  },
  {
    title: "2. Program Oluşturma",
    content: "Analiz sonuçlarına göre size özel bir çalışma programı ve strateji belirleriz.",
    image: "/kocluk-nedir-materials/ders.png",
    badge: "Kişiye Özel Program"
  },
  {
    title: "3. Düzenli Takip",
    content: "Haftalık görüşmeler ve günlük iletişimle sürecinizi yakından takip eder, gerekli düzenlemeleri yaparız. Detaylı deneme analizleri ile eksiklerinizi saptar ve sorunu ortadan kaldırmaya yönelik programlar düzenleriz.",
    image: "/kocluk-nedir-materials/deneme.png",
    badge: "Haftalık Online Görüşme"
  },
  {
    title: "4. Sürekli Destek",
    content: "Sadece akademik değil, motivasyon ve stres yönetimi konularında da yanınızda oluruz. Günlük mesaj takipleriyle öğrenciyi hep kontrol ederiz. Çalışma sürekliliğini alışkanlık haline getiririz.",
    image: "/kocluk-nedir-materials/destek.png",
    badge: "Günlük Mesaj Takibi"
  }
];

export const HowWeWork = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [api, setApi] = useState<any>();
  const [autoplay] = useState<AutoplayType>(
    Autoplay({ delay: 4000, stopOnInteraction: true })
  );

  useEffect(() => {
    if (!api) return;

    api.scrollTo(openIndex);
    autoplay.reset();
  }, [openIndex, api]);

  useEffect(() => {
    if (!api) return;

    api.on("select", () => {
      setOpenIndex(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <section className="py-16 bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12 text-brand-primary">
          Nasıl Çalışıyoruz?
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Left side - Expandable sections */}
          <div className="space-y-4">
            {steps.map((step, index) => (
              <Collapsible
                key={index}
                open={openIndex === index}
                onOpenChange={(isOpen) => {
                  if (isOpen) {
                    setOpenIndex(index);
                  }
                }}
              >
                <CollapsibleTrigger className="flex items-center justify-between w-full p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all">
                  <span className="font-semibold text-lg">{step.title}</span>
                  {openIndex === index ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4 bg-white/50 rounded-lg mt-2">
                  <p className="text-muted-foreground">{step.content}</p>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>

          {/* Right side - Image carousel */}
          <div className="relative">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              plugins={[autoplay]}
              setApi={setApi}
              className="w-full"
            >
              <CarouselContent>
                {steps.map((step, index) => (
                  <CarouselItem key={index}>
                    <div className="p-1">
                      <div className="relative rounded-xl overflow-hidden">
                        <img
                          src={step.image}
                          alt={step.title}
                          className="w-full aspect-video object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "/images/placeholder.png";
                          }}
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-brand-primary text-white px-4 py-2 rounded-full text-sm font-medium">
                            {step.badge}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex items-center justify-center gap-4 mt-4">
                <CarouselPrevious className="relative static" />
                <CarouselNext className="relative static" />
              </div>
            </Carousel>

            {/* Indicators */}
            <div className="flex justify-center gap-2 mt-4">
              {steps.map((_, index) => (
                <button
                  key={index}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    openIndex === index
                      ? "bg-brand-primary w-4"
                      : "bg-brand-primary/20"
                  )}
                  onClick={() => setOpenIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
